import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from '@/stores/redux/store';

export interface LLMModel {
  id: number;
  name: string;
  provider: string;
  model_family?: string;
  is_vision?: boolean;
  alias_model?: string;
  has_web_search?: boolean;
  notes?: string;
  is_tool_use?: boolean;
  is_stt?: boolean;
  is_tts?: boolean;
  is_coding?: boolean;
  is_multilingual?: boolean;
  is_for_conversations?: boolean;
  is_reasoning?: boolean;
  supports_pdf_input?: boolean;
  supports_caching?: boolean;
  supports_thinking_budget?: boolean;
  speed_score?: number;
  api_provider?: string;
}

export interface ModelsState {
  llmModels: LLMModel[];
  loading: boolean;
  error: string | null;
}

const initialState: ModelsState = {
  llmModels: [],
  loading: false,
  error: null,
};

/**
 * Fetch /api/models/llm into the slice. Idempotent — skips if already loaded.
 *
 * Dispatched from the authenticated layout so display names are available
 * everywhere (TeamMessaging sidebar/messages, AiChat picker, etc.) regardless
 * of which page first mounts.
 */
export const ensureLLMModelsLoaded = createAsyncThunk<
  LLMModel[] | null,
  { force?: boolean } | undefined,
  { state: RootState }
>(
  'models/ensureLLMModelsLoaded',
  async (arg, { getState }) => {
    const state = getState();
    if (!arg?.force && state.models.llmModels.length > 0) {
      return null; // already loaded; reducer will no-op
    }
    const resp = await fetch('/api/models/llm');
    if (!resp.ok) throw new Error(`Failed to fetch models: ${resp.status}`);
    return (await resp.json()) as LLMModel[];
  }
);

const modelsSlice = createSlice({
  name: 'models',
  initialState,
  reducers: {
    setLLMModels: (state, action: PayloadAction<LLMModel[]>) => {
      state.llmModels = action.payload;
      state.loading = false;
      state.error = null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(ensureLLMModelsLoaded.pending, (state) => {
        if (state.llmModels.length === 0) state.loading = true;
      })
      .addCase(ensureLLMModelsLoaded.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        if (action.payload) state.llmModels = action.payload;
      })
      .addCase(ensureLLMModelsLoaded.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to load models';
      });
  },
});

export const { setLLMModels, setLoading, setError } = modelsSlice.actions;
export default modelsSlice.reducer;
